import React, { useEffect, useMemo, useState } from 'react'

import { Link, Navigate, useParams } from 'react-router-dom'

import './TokenAssetsList.scss'

import iconSearch from '../../../../../Assets/Icon/AdminTradersCard/mna2n321-ejwdxsi.svg'
import iconFilter from '../../../../../Assets/Icon/AdminTradersCard/mna2n322-dt1dsj6.svg'
import iconExport from '../../../../../Assets/Icon/AdminTradersCard/mna2n322-fjvjkh5.svg'
import iconPrev from '../../../../../Assets/Images/AdminTradersCard/mna2n321-kd3kkq4.png'
import iconNext from '../../../../../Assets/Icon/AdminTradersCard/mna2n322-lk9t334.svg'

import investorTokenAssetsService, {
  type InvestorTokenAsset,
  type InvestorTokenAssetStatus,
} from 'Services/investorTokenAssetsService'
import { TimeConverter } from 'Helpers/TimeConverter'
import { HorizontalLoader } from 'Components'

type TokenAssetStatusRoute = InvestorTokenAssetStatus

const formatNairaMajor = (value: number | undefined | null) => {
  if (value === undefined || value === null) return '-'
  return `₦${value.toLocaleString()}`
}

const TokenAssetsList: React.FC = () => {
  const { status: statusParam } = useParams()
  const activeStatus = (statusParam ?? 'ALL') as TokenAssetStatusRoute

  const isValidStatus =
    activeStatus === 'ALL' ||
    activeStatus === 'ACTIVE' ||
    activeStatus === 'COMPLETED' ||
    activeStatus === 'CANCELLED' ||
    activeStatus === 'LIQUIDATED'

  const [activeTab] = useState<'assets' | 'payouts'>('assets')
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize] = useState(20)
  const [sortOrder] = useState<'ASC' | 'DESC'>('DESC')

  const [q, setQ] = useState('')
  const [appliedQuery, setAppliedQuery] = useState<string>('')

  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [rows, setRows] = useState<InvestorTokenAsset[]>([])
  const [paginationData, setPaginationData] = useState<{
    pageNumber: number
    pageSize: number
    total: number
  }>({ pageNumber: 1, pageSize: 20, total: 0 })

  const getErrorMessage = (e: unknown) => {
    if (typeof e === 'object' && e !== null && 'message' in e) {
      const message = (e as { message?: unknown }).message
      if (typeof message === 'string') return message
      if (message != null) return String(message)
    }
    return null
  }

  const parsedQuery = useMemo(() => {
    const value = appliedQuery.trim()
    if (!value)
      return { accountId: undefined as string | undefined, id: undefined as string | undefined }

    if (/^id:/i.test(value))
      return { id: value.replace(/^id:/i, '').trim() || undefined, accountId: undefined }
    if (/^acct:/i.test(value))
      return { accountId: value.replace(/^acct:/i, '').trim() || undefined, id: undefined }

    const looksLikeUuid = value.length >= 30 && value.includes('-')
    if (looksLikeUuid) return { id: value, accountId: undefined }
    return { accountId: value, id: undefined }
  }, [appliedQuery])

  const fetchAssets = async () => {
    setLoading(true)
    setErrorMessage(null)

    await investorTokenAssetsService
      .list({
        pageNumber,
        pageSize,
        sortOrder,
        status: activeStatus,
        accountId: parsedQuery.accountId,
        id: parsedQuery.id,
      })
      .then(
        (response) => {
          const data = response?.data?.data
          setRows((data?.dataset ?? []) as InvestorTokenAsset[])
          setPaginationData({
            pageNumber: Number(data?.pageNumber ?? pageNumber),
            pageSize: Number(data?.pageSize ?? pageSize),
            total: Number(data?.total ?? 0),
          })
          setLoading(false)
        },
        (e) => {
          setRows([])
          setPaginationData({ pageNumber, pageSize, total: 0 })
          setErrorMessage(getErrorMessage(e) ?? 'Failed to load token assets')

          setLoading(false)
        },
      )
  }

  useEffect(() => {
    if (!isValidStatus) return
    if (activeTab !== 'assets') return

    fetchAssets()
  }, [
    activeStatus,
    activeTab,
    isValidStatus,
    pageNumber,
    pageSize,
    parsedQuery.accountId,
    parsedQuery.id,
    sortOrder,
  ])

  if (!isValidStatus) return <Navigate to='/blockchain/token-assets/ALL' replace />

  const totalPages = Math.max(1, Math.ceil(paginationData.total / paginationData.pageSize))

  return (
    <div className='admin-token-assets'>
      <div className='admin-token-assets__title'>Token Assets</div>

      {activeTab === 'assets' ? (
        <div className='admin-token-assets__tabs'>
          <Link
            to='/blockchain/token-assets/ACTIVE'
            className={[
              'admin-token-assets__tab',
              activeStatus === 'ACTIVE' ? 'admin-token-assets__tab--active' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            ACTIVE
          </Link>
          <Link
            to='/blockchain/token-assets/COMPLETED'
            className={[
              'admin-token-assets__tab',
              activeStatus === 'COMPLETED' ? 'admin-token-assets__tab--active' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            COMPLETED
          </Link>
          <Link
            to='/blockchain/token-assets/CANCELLED'
            className={[
              'admin-token-assets__tab',
              activeStatus === 'CANCELLED' ? 'admin-token-assets__tab--active' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            CANCELLED
          </Link>
          <Link
            to='/blockchain/token-assets/LIQUIDATED'
            className={[
              'admin-token-assets__tab',
              activeStatus === 'LIQUIDATED' ? 'admin-token-assets__tab--active' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            LIQUIDATED
          </Link>
        </div>
      ) : null}

      {activeTab === 'assets' ? (
        <div className='admin-token-assets__toolbar'>
          <form
            className='admin-token-assets__search-form'
            onSubmit={(e) => {
              e.preventDefault()
              setPageNumber(1)
              setAppliedQuery(q)
            }}
          >
            <div className='admin-token-assets-search'>
              <img src={iconSearch} alt='' className='admin-token-assets-search__icon' />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className='admin-token-assets-search__input'
                placeholder='Search (accountId or id:...)'
              />
            </div>
            <button type='submit' className='admin-token-assets-button'>
              <img src={iconFilter} alt='' className='admin-token-assets-button__icon' />
              <span>Search</span>
            </button>
          </form>

          <button type='button' className='admin-token-assets-button'>
            <img src={iconExport} alt='' className='admin-token-assets-button__icon' />
            <span>Export</span>
          </button>
        </div>
      ) : (
        <div className='admin-token-assets__toolbar'></div>
      )}

      <div className='admin-token-assets-table'>
        <div className='table-responsive'>
          <table className='admin-token-assets-table__native w-100'>
            <thead>
              <tr className='admin-token-assets-table__head admin-token-assets-table__grid'>
                <th>Asset ID</th>
                <th>Account ID</th>
                <th>Principal</th>
                <th>Purchase Date</th>
                <th>Status</th>
                <th className='text-end'>Action</th>
              </tr>
            </thead>

            <tbody className='admin-token-assets-table__body'>
              {loading && (
                <tr className='admin-token-assets-table__row admin-token-assets-table__grid'>
                  <td className='admin-token-assets-table__cell text-center' colSpan={6}>
                    <HorizontalLoader></HorizontalLoader>
                  </td>
                </tr>
              )}

              {!loading && errorMessage && (
                <tr className='admin-token-assets-table__row admin-token-assets-table__grid'>
                  <td className='admin-token-assets-table__cell' colSpan={6}>
                    {errorMessage}
                  </td>
                </tr>
              )}

              {!loading && !errorMessage && rows.length === 0 && (
                <tr className='admin-token-assets-table__row admin-token-assets-table__grid'>
                  <td className='admin-token-assets-table__cell' colSpan={6}>
                    No token assets found
                  </td>
                </tr>
              )}

              {!loading &&
                !errorMessage &&
                rows.map((r) => (
                  <tr
                    key={r.id}
                    className='admin-token-assets-table__row admin-token-assets-table__grid'
                  >
                    <td className='admin-token-assets-table__cell'>{r.id}</td>
                    <td className='admin-token-assets-table__cell'>
                      {r.createdByAccountId ?? '-'}
                    </td>
                    <td className='admin-token-assets-table__cell'>
                      {formatNairaMajor(r.principalNairaMajor)}
                    </td>
                    <td className='admin-token-assets-table__cell'>
                      {TimeConverter(r.purchaseDate)}
                    </td>
                    <td className='admin-token-assets-table__cell'>{r.status ?? '-'}</td>
                    <td className='admin-token-assets-table__cell text-end'>
                      <Link
                        to={`/blockchain/token-assets/${activeStatus}/${r.id}`}
                        className='admin-token-assets-table__action'
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <div className='admin-token-assets-pagination'>
          <button
            type='button'
            className='admin-token-assets-pagination__button'
            onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
            disabled={pageNumber <= 1 || loading}
          >
            <img src={iconPrev} alt='' className='admin-token-assets-pagination__icon' />
            <span>Previous</span>
          </button>
          <div className='admin-token-assets-pagination__numbers'>
            Page {pageNumber} of {totalPages}
          </div>
          <button
            type='button'
            className='admin-token-assets-pagination__button'
            onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
            disabled={pageNumber >= totalPages || loading}
          >
            <span>Next</span>
            <img src={iconNext} alt='' className='admin-token-assets-pagination__icon' />
          </button>
        </div>
      </div>
    </div>
  )
}

export default TokenAssetsList
